import { alpha, createTheme } from '@mui/material/styles'
import {
  autocompleteBaseStyles,
  breakpointValues,
  cardBaseStyles,
  dataGridCheckboxStyles,
  formControlStyles,
  formLabelStyles,
  hiddenSearchColumnStyles,
  inputBaseInputStyles,
  inputLabelOutlinedStyles,
  menuStyles,
  paperBaseStyles,
  quillDarkStyles,
  quillLightStyles,
  selectBaseStyles,
  tableContainerStyles,
  tableStyles,
  themeScrollbarStyles,
} from '../tokens/components'

export const grayscaleTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: 'rgba(158, 158, 158, 1)',
      light: 'rgba(207, 207, 207, 1)',
      dark: 'rgba(112, 112, 112, 1)',
      contrastText: 'rgba(255, 255, 255, 1)',
    },
    secondary: {
      main: 'rgba(97, 97, 97, 1)',
      light: 'rgba(142, 142, 142, 1)',
      dark: 'rgba(55, 55, 55, 1)',
      contrastText: 'rgba(238, 238, 238, 1)',
    },
    background: {
      default: 'rgba(30, 30, 30, 1)',
      defaultdim: 'rgba(100, 91, 91, 0.5)',
      paper: 'rgba(42, 42, 42, 1)',
      section: 'rgba(47, 47, 47, 1)',
      sectionContent: 'rgba(60, 60, 60, 1)',
      main: 'rgba(43, 43, 43, 1)',
      tooltip: 'rgba(66, 66, 66, 1)',
      input: 'rgba(56, 56, 56, 1)',
      header: 'rgba(26, 26, 26, 1)',
      selected: 'rgba(51, 51, 51, 1)',
      help: 'rgb(106, 109, 114)',
      details: 'rgba(59, 59, 59, 1)',
      fielddisabled: 'rgba(71, 71, 71, 1)',
      icon: 'rgba(61, 61, 61, 1)',
      dialogtext: 'rgb(26, 25, 25)',
      divider: 'rgba(75, 75, 75, 1)',
      thumbnail: 'rgba(85, 85, 85, 1)',
      disabled: 'rgba(41, 41, 41, 1)',
      overlay: 'rgba(0, 0, 0, 0.9)',
      boxShadow: '0 1px 3px rgba(255, 255, 255, 0.1)',
      loading: 'rgba(143, 143, 143, 1)',
      reviewContent: 'rgba(111, 111, 111, 1)',
      tabs: 'rgba(47, 47, 47, 1)',
      toolbar: 'rgba(36, 39, 46, 1)',
      scrollbar_thumb: 'rgba(167,51,51,1)',
      scrollbar_hover: 'rgb(51, 50, 50)',
      snackbar: '',
      button: 'rgb(33, 100, 160)',
    },
    text: {
      primary: 'rgba(255, 255, 255, 1)',
      secondary: 'rgba(255, 255, 255, 0.6)',
      shadow: 'rgba(0,0,0,0.5)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      tertiary: 'rgba(255, 255, 255, 0.2)',
      // highlight: 'rgba(189, 189, 189, 1)',
      highlight: '#6BB9F0',
      ai_agent_text: '#A0AAB4',
      subtask: 'rgba(176, 176, 176, 1)',
      success: 'rgb(80, 200, 120)',
      successdim: 'rgba(129, 230, 163, 1)',
      warning: 'rgba(255, 224, 130, 1)',
      error: 'rgba(239, 154, 154, 1)',
      errordim: 'rgb(223, 144, 144)',
      resetFilter: 'rgba(255, 204, 128, 1)',
      dirty: 'rgb(85, 123, 155)',
      link: 'rgba(129, 212, 250, 1)',
      revise: 'rgb(255, 131, 88)',
      review: 'rgb(33, 201, 223)',
      chip: 'rgba(224, 224, 224, 1)',
      admin: 'rgba(244, 143, 177, 1)',
      htmlText: 'rgba(255,255,255,1)',
      parent: 'rgba(67, 85, 241, 1)',
    },
    badge: {
      dot: 'rgba(245, 245, 245, 1)',
    },
    tertiary: {
      lighter: 'rgba(238, 238, 238, 1)',
      light: 'rgba(189, 189, 189, 1)',
      main: 'rgba(158, 158, 158, 1)',
      dark: 'rgba(97, 97, 97, 1)',
      darker: 'rgba(33, 33, 33, 1)',
    },
    card: {
      default: 'rgba(35, 35, 35, 1)',
    },
    icon: {
      complete: 'rgba(165, 214, 167, 1)',
      warning: 'rgba(255, 183, 77, 1)',
      decline: 'rgba(239, 154, 154, 1)',
      deals: 'rgba(251, 193, 30, 1)',
      teams: 'rgb(99, 102, 241)',
      organizations: 'rgba(178, 223, 219, 1)',
      approvals: 'rgba(200, 230, 201, 1)',
      contacts: 'rgba(244, 143, 177, 1)',
      nco: 'rgba(128, 203, 196, 1)',
      accounting: 'rgba(144, 202, 249, 1)',
      teamtasks: 'rgba(206, 147, 216, 1)',
      website: 'rgba(121, 134, 203, 1)',
      creative: 'rgba(186, 104, 200, 1)',
      seo: 'rgba(240, 98, 146, 1)',
      mtb: 'rgba(100, 181, 246, 1)',
      products: 'rgb(224, 110, 215)',
      campaignChecklist: 'rgba(255, 171, 145, 1)',
      projects: 'rgba(255, 138, 101, 1)',
      insight: 'rgb(65, 190, 169)',
      observation: 'rgb(168, 202, 255)',
      leasingPercentage: 'rgba(230, 181, 21, 1)',
      pandadoc: 'rgb(36, 133, 103)',
      dashboard: 'rgb(5, 190, 113)',
    },
    myStyledComponentBackground: 'rgba(18, 18, 18, 1)',
    action: {
      selected: alpha('#90caf9', 0.16),
      selectedOpacity: 0.16,
      hoverOpacity: 0.08,
    },
  },
  typography: {
    fontFamily: '"Funnel Sans", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2.5rem',
    },
  },
  spacing: 8,
  hover: '#424242',
  breakpoints: {
    values: breakpointValues,
  },
  shadows: Array(25)
    .fill('none')
    .map((_, index) =>
      index === 0
        ? 'none'
        : `0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.14),
    0px ${index / 2}px ${index}px rgba(0, 0, 0, 0.12),
    0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.2)`
    ) as any,

  components: {
    MyCustomQuill: {
      light: quillLightStyles,
      dark: quillDarkStyles,
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...paperBaseStyles(theme),
          backgroundImage: 'none',
        }),
      },
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          marginRight: '30px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: 'rgba(255,255,255,1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            color: 'rgb(67, 123, 172)',
            backgroundColor: `${alpha(theme.palette.background.reviewContent, 0.25)} !important`,
            fontWeight: 'bold',
          },
          '&.MuiTab-textColorPrimary.Mui-disabled': {
            color: 'rgba(255,255,255,0.4) !important',
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.01)',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,1)',
        },
      },
    },
    MuiTable: {
      styleOverrides: tableStyles,
    },
    MuiTableContainer: {
      styleOverrides: tableContainerStyles,
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(26 22 22)',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {},
      },
    },
    MuiMenu: {
      styleOverrides: menuStyles,
    },
    MuiMenuList: {
      styleOverrides: {
        root: {
          backgroundColor: '#17191E',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {},
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgb(255,255,255,0.5)',
          '&.Mui-focused': {
            color: 'white',
          },
        },
        outlined: inputLabelOutlinedStyles,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          height: 'auto',
          backgroundColor: '#17191E',
          '&:hover': {
            backgroundColor: 'rgb(44, 42, 46)',
            cursor: 'pointer',
          },
        },
        input: {
          ...autocompleteBaseStyles.input,
          color: 'rgba(255,255,255,1)',
        },
        root: autocompleteBaseStyles.root,
        popper: autocompleteBaseStyles.popper,
        paper: autocompleteBaseStyles.paper,
        inputRoot: {
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6cd3e7',
              borderWidth: '1px',
              padding: '0px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'blue',
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {},
        input: {
          ...inputBaseInputStyles,
          color: 'white',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: formControlStyles,
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgb(89, 224, 158)',
            borderWidth: '1px',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...formLabelStyles.root,
          '&.Mui-focused': {
            color: 'rgb(89, 224, 158)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          ...selectBaseStyles,
          '&.Mui-disabled': {},
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.selectMenu .MuiMenuItem-root`]: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '&.Mui-selected': {
            backgroundColor: `${alpha(theme.palette.background.reviewContent, 0.25)} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${alpha(theme.palette.background.reviewContent, 0.2)} !important`,
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          ...cardBaseStyles,
          backgroundColor: 'transparent',
          color: 'rgba(255,255,255,1)',
          ...themeScrollbarStyles,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme: _theme }) => ({
          ...hiddenSearchColumnStyles,
          border: 0,
          background: 'transparent',
          borderColor: 'rgba(255,255,255,0.4)',
          // marginTop: '10px',
          '& .MuiDataGrid-cell': {
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '& > *': {
              transition: 'transform 0.3s ease',
            },
            '&:hover > *': {
              transform: 'scale(1.07)',
            },
          },
          // Scrollbar styles
          '& .MuiDataGrid-scrollbar': {
            scrollbarWidth: 'auto',
            scrollbarColor: `${_theme.palette.text.primary} ${_theme.palette.background.default}`,
          },
          '&': {
            scrollbarColor: `${_theme.palette.text.primary} ${_theme.palette.background.default}`,
          },
          // Disable cell hover transform for cells containing avatars - let EntityAvatar handle its own hover
          '& .MuiDataGrid-cell:has(.MuiAvatar-root), & .MuiDataGrid-cell:has(.MuiAvatarGroup-root)': {
            overflow: 'visible !important', // Allow avatars to scale without being clipped
          },
          '& .MuiDataGrid-cell:has(.MuiAvatar-root):hover, & .MuiDataGrid-cell:has(.MuiAvatarGroup-root):hover': {
            transform: 'none',
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-columnHeaders': {
            fontSize: '14px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#fafafa',
          },
          '& .MuiDataGrid-row': {
            fontSize: '12px',
            '&:hover': {
              backgroundColor: 'inherit',
            },
          },
          '.MuiDataGrid-row.Mui-selected': {
            '&:hover': {
              backgroundColor: 'rgba(87, 125, 173, 0.4)',
            },
            backgroundColor: 'rgba(87, 125, 173, 0.4)',
          },
          ...dataGridCheckboxStyles,
          '& .MuiDataGrid-pinnedRows--bottom': {
            p: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            backgroundColor: 'rgb(49, 57, 70)',
            width: '100%',
            minWidth: '100%',
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            backgroundColor: `transparent !important`,
          },
          '& .MuiDataGrid-filler--pinnedLeft': {
            backgroundColor: `transparent !important`,
          },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: 'rgb(49, 57, 70)',
            overflow: 'visible',
            position: 'relative',
            zIndex: 10,
          },
          '& .MuiDataGrid-toolbarContainer .MuiToolbar-root': {
            overflow: 'visible',
            paddingTop: '12px',
            minHeight: '56px',
          },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: 'rgb(49, 57, 70)',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: '4px solid rgb(197, 39, 39)',
          },
          // Aggregation row styling
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: `${_theme.palette.background.defaultdim} !important`,
          },
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"] .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row--lastVisible[data-id="auto-generated-group-footer-root"]': {
            zIndex: 100,
            backgroundColor: _theme.palette.background.defaultdim,
            width: '100%',
            pointerEvents: 'none',
          },
          // Pinned columns - custom background with visible borders
          '& .MuiDataGrid-pinnedColumns--left, & .MuiDataGrid-pinnedColumns--right': {
            backgroundColor: _theme.palette.background.input,
          },
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `${_theme.palette.background.input} !important`,
            // Add visible borders using boxShadow to match grid styling
            boxShadow: `
              1px 0 0 0 ${alpha(_theme.palette.divider, 0.12)},
              0 1px 0 0 ${alpha(_theme.palette.divider, 0.12)}
            `,
          },
          // Maintain pinned column borders on hover
          '& .MuiDataGrid-cell--pinnedLeft:hover, & .MuiDataGrid-cell--pinnedRight:hover': {
            boxShadow: `
              1px 0 0 0 ${alpha(_theme.palette.divider, 0.12)},
              0 1px 0 0 ${alpha(_theme.palette.divider, 0.12)}
            `,
          },
          // Pinned column headers with borders
          '& .MuiDataGrid-pinnedColumnHeaders--left, & .MuiDataGrid-pinnedColumnHeaders--right': {
            backgroundColor: _theme.palette.background.input,
          },
          '& .MuiDataGrid-pinnedColumnHeaders--left .MuiDataGrid-columnHeader, & .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-columnHeader': {
            backgroundColor: `${_theme.palette.background.input} !important`,
            boxShadow: `
              1px 0 0 0 ${alpha(_theme.palette.divider, 0.12)},
              0 1px 0 0 ${alpha(_theme.palette.divider, 0.12)}
            `,
          },
          // Fix pinned column background in aggregation row
          '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: _theme.palette.background.paper,
          },
          // Virtual scroller backgrounds
          '& .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-virtualScrollerRenderZone': {
            backgroundColor: 'transparent',
          },
          // Bottom container styling
          '& .MuiDataGrid-container--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          '& .MuiDataGrid-scrollArea--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          // Bottom pinned rows styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-cell': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
          },
          // Server aggregation row styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
            fontWeight: 600,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
            pointerEvents: 'none',
          },
          // Disable hover effects on server aggregation row
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover .MuiDataGrid-cell': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
          },
          // Disable cell hover effects on aggregation row
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell:hover': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          // ============================================================================
          // Row Classes - Special row styling
          // ============================================================================
          '& .high-priority-row': {
            backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
          },
          '& .postdated': {
            backgroundColor: 'rgba(255, 193, 7, 0.15) !important',
          },
          '& .postdated:hover': {
            backgroundColor: 'rgba(255, 193, 7, 0.15) !important',
          },
          '& .postdated .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .postdated .MuiDataGrid-cell:hover': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(201, 52, 52, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .MuiDataGrid-row.Mui-selected.MuiDataGrid-row--firstVisible.MuiDataGrid-row--lastVisible.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(228, 22, 22, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .task-row': {
            backgroundColor: 'rgba(100, 100, 100, 0.1)',
            fontWeight: 600,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          },
          '& .subtask-row': {
            backgroundColor: _theme.palette.background.default,
            paddingLeft: '32px',
          },
          '& .disabled-selection': {
            opacity: '0.3 !important',
            pointerEvents: 'none',
            backgroundColor: 'rgba(100, 100, 100, 0.05) !important',
          },
          '& .product-row': {
            backgroundColor: 'rgba(100, 100, 100, 0.1)',
          },
          '& .MuiDataGrid-row.delete-row': {
            border: '1px solid',
            borderColor: 'rgb(238, 124, 124) !important',
            transition: 'background-color 0.3s ease',
            zIndex: '1900 !important',
            position: 'relative !important',
          },
          // ============================================================================
          // Org Name Header Styling
          // ============================================================================
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainer': {
            flex: 1,
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainerContent': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-iconButtonContainer': {
            flexShrink: 0,
          },
          '& .org-name-header .MuiStack-root': {
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiTypography-root': {
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiBox-root': {
            marginLeft: 'auto',
            display: 'flex',
            gap: '8px',
            flex: '0 0 auto',
          },
          // ============================================================================
          // Pinned Rows Top Styling
          // ============================================================================
          '& .MuiDataGrid-pinnedRows, & .MuiDataGrid-pinnedRows--top': {
            borderRadius: '0 !important',
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            borderRadius: '0 !important',
            backgroundColor: _theme.palette.background.paper,
          },
          '& .MuiDataGrid-container--top': {
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
            overflow: 'visible',
          },
          '& .MuiDataGrid-container--top [role="row"]': {
            border: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--top': {
            paddingBottom: '22px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            paddingTop: '2px',
            paddingBottom: '2px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // ============================================================================
          // Pinned Rows Bottom - Additional Styling
          // ============================================================================
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerContent': {
            backgroundColor: `${_theme.palette.background.paper} !important`,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // ============================================================================
          // Drawer Open State - Dim rows except current
          // ============================================================================
          '&.has-drawer-open .MuiDataGrid-row': {
            position: 'relative',
            opacity: 0.9,
          },
          '&.has-drawer-open .MuiDataGrid-row:not(.drawer-current-row)::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .drawer-current-row': {
            isolation: 'isolate',
            zIndex: 1,
            opacity: '1 !important',
          },
          '&.has-drawer-open .drawer-current-row .MuiDataGrid-cell': {
            backgroundColor: 'rgba(87, 125, 173, 0.4)',
          },
          '&.has-drawer-open .MuiDataGrid-columnHeaders, &.has-drawer-open .MuiDataGrid-toolbarContainer, &.has-drawer-open .MuiDataGrid-footerContainer, &.has-drawer-open .MuiDataGrid-toolbar, &.has-drawer-open .MuiDataGrid-cellCheckbox': {
            opacity: 0.1,
            backgroundColor: 'transparent',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .MuiDataGrid-row:hover': {
            opacity: 1,
            backgroundColor: 'rgba(87, 125, 173, 0.2) !important',
          },
          // ============================================================================
          // Mass Update Highlighting - Editable cells when multiple rows selected
          // ============================================================================
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target': {
            backgroundColor: 'rgba(24, 144, 255, 0.12)',
            borderLeft: '2px solid rgba(24, 144, 255, 0.4)',
          },
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target:hover': {
            backgroundColor: 'rgba(24, 144, 255, 0.2)',
          },
          '& .MuiDataGrid-cell--editing.mass-update-target': {
            backgroundColor: 'rgba(24, 144, 255, 0.2) !important',
            boxShadow: 'inset 0 0 0 2px rgba(24, 144, 255, 0.5)',
          },
          // ============================================================================
          // NCO Expand Button
          // ============================================================================
          '& .nco-expand-button': {
            pointerEvents: 'var(--nco-expand-events) !important',
            color: 'var(--nco-expand-color) !important',
          },
        }),
        pagination: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        footerContainer: ({ theme }) => ({
          backgroundColor: theme.palette.background.defaultdim,
          '& .MuiTablePagination-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiTablePagination-toolbar': {
            backgroundColor: 'transparent',
          },
          '& .MuiToolbar-root': {
            backgroundColor: 'transparent',
          },
        }),
      },
    },
  },
})
